/** ... keep header comment ... */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';

import useApi from '../hooks/useApi';
import Footer from '../components/Footer';

const INITIAL_CUSTOMER_DETAILS = {
  name: '',
  email: '',
  phoneNumber: '',
  verificationCode: '',
  specialNote: '',
};

const DEFAULT_MAX_PARTY_SIZE = 4;
const US_COUNTRY_CODE = '+1';
const PHONE_NUMBER_LENGTH = 10;
const PLUTO_LOGO_PATH = `${process.env.PUBLIC_URL || ''}/PlutoTV_Logo_1080x1080_RGB_Plutonium.ai.png`;
const GENERIC_ERROR_FALLBACK = 'Something went wrong. Please try again.';
const TECHNICAL_ERROR_INDICATORS = [
  'api error',
  'verificationcheck',
  'requested resource',
  'internal server error',
  'exception',
  'twilio',
];
const WAITLIST_SUCCESS_FALLBACK =
  'You have been added to the waitlist. We will notify you as soon as a session opens up.';

const rowsFromResponse = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.rooms)) {
    return response.rooms;
  }
  if (Array.isArray(response.data?.rooms)) {
    return response.data.rooms;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const resolveCityName = (entity) => {
  if (!entity) {
    return 'Unknown City';
  }
  const cityValue =
    entity.city ||
    entity.room?.city ||
    entity.locationCity ||
    entity.location?.city ||
    entity.room?.locationCity ||
    '';
  if (!cityValue) {
    return 'Unknown City';
  }
  return cityValue;
};

const resolveRoomId = (room) => {
  if (!room) {
    return '';
  }
  const candidate =
    room.id ??
    room.roomId ??
    room._id ??
    room.uuid ??
    room.room?.id ??
    room.room?.roomId ??
    null;
  if (candidate === null || candidate === undefined) {
    return '';
  }
  return String(candidate);
};

const bookingFromResponse = (response) => {
  if (!response) {
    return null;
  }
  if (response.booking) {
    return response.booking;
  }
  if (response.data?.booking) {
    return response.data.booking;
  }
  return null;
};

const allocatedSlotsFromResponse = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.allocatedSlotLabels)) {
    return response.allocatedSlotLabels;
  }
  if (Array.isArray(response.data?.allocatedSlotLabels)) {
    return response.data.allocatedSlotLabels;
  }
  const booking = bookingFromResponse(response);
  if (Array.isArray(booking?.slotLabels)) {
    return booking.slotLabels;
  }
  return [];
};

const resolveGuestCountFromResponse = (response, fallback) => {
  const booking = bookingFromResponse(response);
  if (booking?.slotQuantity) {
    return booking.slotQuantity;
  }
  if (response?.totalGuests) {
    return response.totalGuests;
  }
  if (response?.data?.totalGuests) {
    return response.data.totalGuests;
  }
  const allocatedSlots = allocatedSlotsFromResponse(response);
  if (allocatedSlots.length) {
    return allocatedSlots.length;
  }
  return fallback;
};

const formatDisplayDate = (value) => {
  if (!value) {
    return 'Date TBD';
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatDisplayTime = (timeSlot) => {
  if (!timeSlot) {
    return 'Time TBD';
  }
  const [hourStr, minuteStr] = timeSlot.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return timeSlot;
  }
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatVerificationExpiry = (value) => {
  if (!value) {
    return '';
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const slotDateValue = (slot) => {
  if (!slot?.date) {
    return Number.NaN;
  }
  const dateString = `${slot.date}T${slot.timeSlot || '00:00'}:00`;
  const parsed = Date.parse(dateString);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  return Date.parse(slot.date);
};

const getSlotIdentifier = (slot) => {
  if (!slot) {
    return 'unknown-slot';
  }
  if (slot.id) {
    return slot.id;
  }
  return `${slot.roomId}|${slot.date}|${slot.sessionLabel}|${slot.slotLabel}`;
};

const normalizePhoneNumber = (value) => `${value || ''}`.replace(/\D/g, '');

const formatPhoneNumberForRequest = (rawPhoneNumber) => {
  const digitsOnly = normalizePhoneNumber(rawPhoneNumber);
  if (digitsOnly.length !== PHONE_NUMBER_LENGTH) {
    return null;
  }
  return `${US_COUNTRY_CODE}${digitsOnly}`;
};

const isValidUsPhoneNumber = (value) => normalizePhoneNumber(value).length === PHONE_NUMBER_LENGTH;

const sortSlots = (a, b) => {
  const aValue = slotDateValue(a);
  const bValue = slotDateValue(b);
  if (!Number.isNaN(aValue) && !Number.isNaN(bValue)) {
    return aValue - bValue;
  }
  if (!Number.isNaN(aValue)) {
    return -1;
  }
  if (!Number.isNaN(bValue)) {
    return 1;
  }
  return getSlotIdentifier(a).localeCompare(getSlotIdentifier(b));
};

const aggregateSlotsBySession = (slots = []) => {
  const grouped = new Map();

  slots.forEach((slot) => {
    if (!slot) {
      return;
    }
    const key = [
      slot.roomId || 'room',
      slot.date || 'date',
      slot.sessionLabel || slot.timeSlot || 'session',
      slot.timeSlot || 'time',
    ].join('|');

    if (!grouped.has(key)) {
      grouped.set(key, {
        ...slot,
        id: key,
        availableSlotLabels: [],
        totalRemainingCapacity: 0,
      });
    }

    const session = grouped.get(key);

    if (slot.slotLabel) {
      session.availableSlotLabels.push(slot.slotLabel);
    }

    const remainingValue = Number(
      slot.remainingCapacity ??
        slot.capacity ??
        slot.sessionCapacity ??
        slot.capacity ??
        slot.sessionCapacity ??
        slot.capacity ??
        slot.sessionCapacity ??
        slot.capacity ??
        slot.sessionCapacity ??
        slot.capacity ??
        slot.sessionCapacity ??
        0
    );

    if (!Number.isNaN(remainingValue) && remainingValue > 0) {
      session.totalRemainingCapacity += remainingValue;
    }
  });

  return Array.from(grouped.values())
    .map((session) => ({
      ...session,
      slotLabel: session.availableSlotLabels[0] || session.slotLabel || null,
      remainingCapacity: session.totalRemainingCapacity,
      totalRemainingCapacity: session.totalRemainingCapacity,
      availableSlotLabels: session.availableSlotLabels,
    }))
    .sort(sortSlots);
};

const resolveRemainingCapacity = (slot) => {
  if (!slot) {
    return 0;
  }
  const value = Number(
    slot.totalRemainingCapacity ?? slot.remainingCapacity ?? slot.sessionCapacity ?? slot.capacity ?? 0
  );
  if (Number.isNaN(value) || value <= 0) {
    return 0;
  }
  return value;
};

const collectErrorMessageCandidates = (error) => {
  if (!error) {
    return [];
  }
  if (typeof error === 'string') {
    return [error.trim()].filter(Boolean);
  }
  const candidates = [
    error.userMessage,
    error.friendlyMessage,
    error.message,
    error.response?.data?.userMessage,
    error.response?.data?.message,
    error.response?.data?.error?.message,
    error.response?.message,
    error.statusText,
  ];
  return candidates
    .filter((candidate) => typeof candidate === 'string')
    .map((candidate) => candidate.trim())
    .filter(Boolean);
};

const isFriendlyErrorMessage = (message) => {
  if (!message) {
    return false;
  }
  if (message.length > 200) {
    return false;
  }
  const normalized = message.toLowerCase();
  if (TECHNICAL_ERROR_INDICATORS.some((indicator) => normalized.includes(indicator))) {
    return false;
  }
  if ((message.includes('{') && message.includes('}')) || (message.includes('[') && message.includes(']'))) {
    return false;
  }
  return true;
};

const getFriendlyErrorMessage = (error, fallback = GENERIC_ERROR_FALLBACK) => {
  const candidates = collectErrorMessageCandidates(error);
  const friendlyCandidate = candidates.find((candidate) => isFriendlyErrorMessage(candidate));
  return friendlyCandidate || fallback;
};

const BookingPage = () => {
  const navigate = useNavigate();
  const { rageRoom } = useApi();
  const locationFilter = (process.env.REACT_APP_DEFAULT_LOCATION_ID || '').trim();

  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(null);

  const [roomsDirectory, setRoomsDirectory] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState(null);

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [partySize, setPartySize] = useState(1);
  const [customerDetails, setCustomerDetails] = useState({ ...INITIAL_CUSTOMER_DETAILS });

  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccessMessage, setWaitlistSuccessMessage] = useState(null);
  const [waitlistError, setWaitlistError] = useState(null);

  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [codeRequestMessage, setCodeRequestMessage] = useState(null);
  const [codeRequestError, setCodeRequestError] = useState(null);

  const [verificationToken, setVerificationToken] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationExpiresAt, setVerificationExpiresAt] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  const [hasAcknowledgedTerms, setHasAcknowledgedTerms] = useState(false);
  const [hasConfirmedAgeRequirement, setHasConfirmedAgeRequirement] = useState(false);

  const resetVerificationState = useCallback(() => {
    setVerificationToken(null);
    setVerificationStatus(null);
    setVerificationExpiresAt(null);
  }, []);

  const loadAvailability = useCallback(async () => {
    if (!rageRoom?.public?.listAvailability) {
      setAvailabilityError('Availability service is not configured.');
      setAvailabilityLoading(false);
      return;
    }

    setAvailabilityLoading(true);
    setAvailabilityError(null);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const rangeEnd = new Date(today);
      rangeEnd.setDate(rangeEnd.getDate() + 14);
      const endDate = rangeEnd.toISOString().split('T')[0];

      const availabilityParams = {
        startDate,
        endDate,
        onlyAvailable: true,
      };

      if (locationFilter) {
        availabilityParams.locationId = locationFilter;
      }

      const response = await rageRoom.public.listAvailability(availabilityParams);

      setAvailabilityRows(rowsFromResponse(response));
    } catch (error) {
      setAvailabilityError(
        getFriendlyErrorMessage(
          error,
          'Unable to load availability right now. Please refresh in a moment.'
        )
      );
      setAvailabilityRows([]);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [locationFilter]);

  const loadRoomsDirectory = useCallback(async () => {
    if (!rageRoom?.admin?.listRooms) {
      setRoomsError('Room directory service is not configured.');
      setRoomsDirectory([]);
      setRoomsLoading(false);
      return;
    }

    setRoomsLoading(true);
    setRoomsError(null);

    try {
      const response = await rageRoom.admin.listRooms({ locationId: 'default-location' });
      setRoomsDirectory(rowsFromResponse(response));
    } catch (error) {
      setRoomsDirectory([]);
      setRoomsError(
        getFriendlyErrorMessage(
          error,
          'Unable to load the rage room directory. Please refresh in a moment.'
        )
      );
    } finally {
      setRoomsLoading(false);
    }
  }, [locationFilter]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  useEffect(() => {
    loadRoomsDirectory();
  }, [loadRoomsDirectory]);

  const refreshData = useCallback(() => {
    loadAvailability();
    loadRoomsDirectory();
  }, [loadAvailability, loadRoomsDirectory]);

  const isCityListLoading = availabilityLoading || roomsLoading;

  const cities = useMemo(() => {
    const unique = new Set();
    const ordered = [];

    [...roomsDirectory, ...availabilityRows].forEach((row) => {
      const cityName = resolveCityName(row);
      if (!cityName || unique.has(cityName)) {
        return;
      }
      unique.add(cityName);
      ordered.push(cityName);
    });

    return ordered.sort((a, b) => a.localeCompare(b));
  }, [availabilityRows, roomsDirectory]);

  const filteredRowsForCity = useMemo(() => {
    if (!selectedCity) {
      return [];
    }
    return availabilityRows.filter((row) => resolveCityName(row) === selectedCity);
  }, [availabilityRows, selectedCity]);

  const roomDirectoryIndex = useMemo(() => {
    const index = new Map();
    roomsDirectory.forEach((room) => {
      const roomId = resolveRoomId(room);
      if (roomId) {
        index.set(roomId, room);
      }
    });
    return index;
  }, [roomsDirectory]);

  const roomsByDateForSelectedCity = useMemo(() => {
    if (!selectedCity) {
      return [];
    }

    const groupedByDate = new Map();

    filteredRowsForCity.forEach((row) => {
      if (!row?.date) {
        return;
      }
      const dateKey = row.date;
      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, []);
      }
      groupedByDate.get(dateKey).push(row);
    });

    const dateGroups = Array.from(groupedByDate.entries()).map(([date, rowsForDate]) => {
      const roomsGrouped = new Map();

      rowsForDate.forEach((row) => {
        const roomId = resolveRoomId(row);
        if (!roomId) {
          return;
        }
        if (!roomsGrouped.has(roomId)) {
          roomsGrouped.set(roomId, []);
        }
        roomsGrouped.get(roomId).push(row);
      });

      const rooms = Array.from(roomsGrouped.entries())
        .map(([roomId, rowsForRoom]) => {
          const sortedSlots = [...rowsForRoom].sort(sortSlots);
          const primary = sortedSlots[0] || rowsForRoom[0];
          const directoryMatch = roomDirectoryIndex.get(roomId);
          return {
            id: roomId,
            date,
            displayDate: formatDisplayDate(date),
            name:
              directoryMatch?.name ||
              directoryMatch?.roomName ||
              primary?.roomName ||
              'Rage Room',
            city: resolveCityName(directoryMatch || primary),
            locationId:
              primary?.locationId ||
              primary?.room?.locationId ||
              directoryMatch?.locationId ||
              directoryMatch?.location?.id ||
              '',
            locationName:
              primary?.room?.locationName ||
              primary?.locationName ||
              directoryMatch?.locationName ||
              directoryMatch?.location?.name ||
              '',
            locationAddress:
              primary?.room?.locationAddress ||
              primary?.locationAddress ||
              directoryMatch?.locationAddress ||
              directoryMatch?.location?.address ||
              '',
            slots: rowsForRoom,
            availableCount: rowsForRoom.length,
            nextSlot: sortedSlots[0] || null,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      const sortedRowsForDate = [...rowsForDate].sort(sortSlots);
      const timestamp = sortedRowsForDate.length ? slotDateValue(sortedRowsForDate[0]) : Number.NaN;

      return {
        date,
        displayDate: formatDisplayDate(date),
        timestamp,
        rooms,
      };
    });

    return dateGroups
      .filter((group) => group.rooms.length > 0)
      .sort((a, b) => {
        if (!Number.isNaN(a.timestamp) && !Number.isNaN(b.timestamp)) {
          return a.timestamp - b.timestamp;
        }
        if (!Number.isNaN(a.timestamp)) {
          return -1;
        }
        if (!Number.isNaN(b.timestamp)) {
          return 1;
        }
        return a.date.localeCompare(b.date);
      });
  }, [filteredRowsForCity, roomDirectoryIndex, selectedCity]);

  const directoryRoomsForSelectedCity = useMemo(() => {
    if (!selectedCity) {
      return [];
    }

    const directoryMatches = roomsDirectory.filter((room) => resolveCityName(room) === selectedCity);

    return directoryMatches
      .map((roomData) => {
        const normalizedRoomId = resolveRoomId(roomData);
        const roomAvailability = normalizedRoomId
          ? availabilityRows.filter((row) => String(row.roomId) === normalizedRoomId)
          : [];
        const sortedSlots = [...roomAvailability].sort(sortSlots);
        const primaryAvailabilityRow = roomAvailability[0] || null;

        return {
          id: normalizedRoomId || roomData.id || roomData.roomId || '',
          name: roomData.name || roomData.roomName || primaryAvailabilityRow?.roomName || 'Rage Room',
          city: resolveCityName(roomData),
          locationId:
            roomData.locationId ||
            roomData.location?.id ||
            primaryAvailabilityRow?.locationId ||
            primaryAvailabilityRow?.room?.locationId ||
            '',
          locationName:
            roomData.locationName ||
            roomData.location?.name ||
            primaryAvailabilityRow?.room?.locationName ||
            primaryAvailabilityRow?.locationName ||
            '',
          locationAddress:
            roomData.locationAddress ||
            roomData.location?.address ||
            primaryAvailabilityRow?.room?.locationAddress ||
            primaryAvailabilityRow?.locationAddress ||
            '',
          slots: roomAvailability,
          availableCount: roomAvailability.length,
          nextSlot: sortedSlots[0] || null,
          date: null,
          displayDate: '',
        };
      })
      .filter((room) => Boolean(room.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availabilityRows, roomsDirectory, selectedCity]);

  const availableSlotsForRoom = useMemo(() => {
    if (!selectedRoomId) {
      return [];
    }
    const slotsForRoom = availabilityRows.filter((row) => {
      if (String(row.roomId) !== selectedRoomId) {
        return false;
      }
      if (selectedDate) {
        return row.date === selectedDate;
      }
      return true;
    });
    return aggregateSlotsBySession(slotsForRoom);
  }, [availabilityRows, selectedDate, selectedRoomId]);

  const selectedRoom = useMemo(() => {
    if (selectedRoomId && selectedDate) {
      const dateGroup = roomsByDateForSelectedCity.find((group) => group.date === selectedDate);
      return dateGroup?.rooms?.find((room) => room.id === selectedRoomId) || null;
    }
    if (selectedRoomId && !selectedDate) {
      return directoryRoomsForSelectedCity.find((room) => room.id === selectedRoomId) || null;
    }
    return null;
  }, [
    directoryRoomsForSelectedCity,
    roomsByDateForSelectedCity,
    selectedDate,
    selectedRoomId,
  ]);

  useEffect(() => {
    if (!selectedRoomId && !selectedDate) {
      return;
    }

    if (selectedRoomId && selectedDate) {
      const dateGroup = roomsByDateForSelectedCity.find((group) => group.date === selectedDate);
      const hasRoom = dateGroup?.rooms?.some((room) => room.id === selectedRoomId);
      if (!hasRoom) {
        setSelectedRoomId('');
        setSelectedDate('');
        setSelectedSlot(null);
      }
      return;
    }

    if (selectedRoomId && !selectedDate) {
      const hasDirectoryRoom = directoryRoomsForSelectedCity.some((room) => room.id === selectedRoomId);
      if (!hasDirectoryRoom) {
        setSelectedRoomId('');
        setSelectedSlot(null);
      }
    }
  }, [
    directoryRoomsForSelectedCity,
    roomsByDateForSelectedCity,
    selectedDate,
    selectedRoomId,
  ]);

  const hasAvailableSlots = availableSlotsForRoom.length > 0;
  const shouldUseWaitlistAction = Boolean(selectedRoom && !hasAvailableSlots);

  const selectionSummary = useMemo(() => {
    if (!selectedSlot) {
      return '';
    }
    return `${selectedSlot.roomName} • ${formatDisplayDate(selectedSlot.date)} @ ${formatDisplayTime(
      selectedSlot.timeSlot
    )}`;
  }, [selectedSlot]);

  const isVerificationComplete = useMemo(
    () => Boolean(verificationToken && verificationStatus === 'approved'),
    [verificationStatus, verificationToken]
  );

  const isPhoneNumberValid = useMemo(
    () => isValidUsPhoneNumber(customerDetails.phoneNumber),
    [customerDetails.phoneNumber]
  );

  const phoneDisclosureText =
    'Your phone number will only be used to confirm your reservation or address other matters in regard to this event.';

  const phoneHelperText = isPhoneNumberValid
    ? `We'll text your verification code and confirmation here. ${phoneDisclosureText}`
    : `Enter a ${PHONE_NUMBER_LENGTH}-digit US phone number. ${phoneDisclosureText}`;

  const verificationHelperText = isVerificationComplete
    ? `Phone verified${
        formatVerificationExpiry(verificationExpiresAt)
          ? ` • Expires at ${formatVerificationExpiry(verificationExpiresAt)}`
          : ''
      }`
    : 'Enter the SMS code we texted after tapping "Send Code".';

  const canSubmitBooking = useMemo(() => {
    if (!selectedSlot) {
      return false;
    }
    return Boolean(
      customerDetails.name.trim() &&
        customerDetails.email.trim() &&
        customerDetails.verificationCode.trim() &&
        isVerificationComplete &&
        isPhoneNumberValid &&
        hasAcknowledgedTerms &&
        hasConfirmedAgeRequirement
    );
  }, [
    customerDetails.email,
    customerDetails.name,
    customerDetails.verificationCode,
    hasAcknowledgedTerms,
    hasConfirmedAgeRequirement,
    isPhoneNumberValid,
    isVerificationComplete,
    selectedSlot,
  ]);

  const canJoinWaitlist = useMemo(() => {
    if (!selectedRoom) {
      return false;
    }
    return Boolean(
      customerDetails.name.trim() &&
        customerDetails.email.trim() &&
        customerDetails.verificationCode.trim() &&
        isVerificationComplete &&
        isPhoneNumberValid &&
        hasAcknowledgedTerms &&
        hasConfirmedAgeRequirement
    );
  }, [
    customerDetails.email,
    customerDetails.name,
    customerDetails.verificationCode,
    hasAcknowledgedTerms,
    hasConfirmedAgeRequirement,
    isPhoneNumberValid,
    isVerificationComplete,
    selectedRoom,
  ]);

  const selectedSlotRemainingCapacity = useMemo(
    () => resolveRemainingCapacity(selectedSlot),
    [selectedSlot]
  );

  const maxRoomSessionPartySize = useMemo(() => {
    if (!availableSlotsForRoom.length) {
      return DEFAULT_MAX_PARTY_SIZE;
    }
    const highestCapacity = availableSlotsForRoom.reduce((max, slot) => {
      const remaining = resolveRemainingCapacity(slot);
      return remaining > max ? remaining : max;
    }, 0);
    return highestCapacity > 0 ? highestCapacity : DEFAULT_MAX_PARTY_SIZE;
  }, [availableSlotsForRoom]);

  const partySizeHelperText = useMemo(() => {
    const baseLimitText = `Plan for up to ${maxRoomSessionPartySize} guest${
      maxRoomSessionPartySize === 1 ? '' : 's'
    }.`;
    if (!selectedSlot) {
      return `${baseLimitText} Sessions that can't fit your crew will be greyed out once you unlock availability.`;
    }
    const capacityText = selectedSlotRemainingCapacity
      ? `${selectedSlotRemainingCapacity} spot${
          selectedSlotRemainingCapacity === 1 ? '' : 's'
        } remain in this session right now.`
      : 'Capacity updates in real time for this session.';
    return `${baseLimitText} Sessions without at least ${partySize} seats will stay disabled. ${capacityText}`;
  }, [maxRoomSessionPartySize, partySize, selectedSlot, selectedSlotRemainingCapacity]);

  const crewAlertMessage = useMemo(() => {
    if (selectedSlot) {
      return `${selectionSummary} • 30-minute private run for up to ${maxRoomSessionPartySize} people. ${
        selectedSlotRemainingCapacity
          ? `${selectedSlotRemainingCapacity} spot${
              selectedSlotRemainingCapacity === 1 ? '' : 's'
            } remain for this session.`
          : 'Capacity updates in real time for this session.'
      } Sessions without enough room for your crew of ${partySize} are greyed out in Step 4.`;
    }
    if (selectedRoom) {
      const roomLabel = selectedRoom.displayDate
        ? `${selectedRoom.name} on ${selectedRoom.displayDate}`
        : selectedRoom.name;
      return `Tell us who is smashing at ${roomLabel}. Verify your phone to unlock sessions and reserve up to ${maxRoomSessionPartySize} guest${
        maxRoomSessionPartySize === 1 ? '' : 's'
      }. Slots that can't fit your crew will disable automatically.`;
    }
    return 'Share your crew details, verify your phone number, and then lock a time slot once you pick a city and room.';
  }, [
    maxRoomSessionPartySize,
    partySize,
    selectedRoom,
    selectedSlot,
    selectedSlotRemainingCapacity,
    selectionSummary,
  ]);

  useEffect(() => {
    setPartySize((prev) => {
      const limit = Math.max(1, maxRoomSessionPartySize);
      if (prev > limit) {
        return limit;
      }
      if (prev < 1) {
        return 1;
      }
      return prev;
    });
  }, [maxRoomSessionPartySize]);

  useEffect(() => {
    if (selectedSlot && partySize > selectedSlotRemainingCapacity) {
      setSelectedSlot(null);
    }
  }, [partySize, selectedSlot, selectedSlotRemainingCapacity]);

  useEffect(() => {
    if (availableSlotsForRoom.length > 0) {
      setWaitlistSuccessMessage(null);
      setWaitlistError(null);
      setWaitlistLoading(false);
    }
  }, [availableSlotsForRoom.length]);

  const handleCitySelection = (city) => {
    setSelectedCity(city);
    setSelectedRoomId('');
    setSelectedDate('');
    setSelectedSlot(null);
    setBookingError(null);
    setHasAcknowledgedTerms(false);
    setHasConfirmedAgeRequirement(false);
    setWaitlistSuccessMessage(null);
    setWaitlistError(null);
    setWaitlistLoading(false);
  };

  const handleRoomSelection = (roomId, date) => {
    const normalizedRoomId = roomId ? String(roomId) : '';
    const normalizedDate = date || '';
    setSelectedRoomId(normalizedRoomId);
    setSelectedDate(normalizedDate);
    setSelectedSlot(null);
    setBookingError(null);
    setHasAcknowledgedTerms(false);
    setHasConfirmedAgeRequirement(false);
    setWaitlistSuccessMessage(null);
    setWaitlistError(null);
    setWaitlistLoading(false);
  };

  const handleSlotSelection = (slot) => {
    if (!isVerificationComplete) {
      return;
    }
    const remainingCapacity = resolveRemainingCapacity(slot);
    if (partySize > remainingCapacity) {
      return;
    }
    setSelectedSlot(slot);
    setBookingError(null);
  };

  const handleCustomerChange = (field) => (event) => {
    const { value } = event.target;
    setWaitlistSuccessMessage(null);
    setWaitlistError(null);
    const nextValue =
      field === 'phoneNumber' ? normalizePhoneNumber(value).slice(0, PHONE_NUMBER_LENGTH) : value;
    setCustomerDetails((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    if (field === 'phoneNumber') {
      setCodeRequestMessage(null);
      setCodeRequestError(null);
    }
    if (
      (field === 'phoneNumber' || field === 'verificationCode') &&
      (verificationToken || verificationStatus)
    ) {
      resetVerificationState();
      setVerificationError(null);
      setSelectedSlot(null);
    }
  };

  const handlePartySizeChange = (event) => {
    const value = Number(event.target.value);
    if (Number.isNaN(value)) {
      setPartySize(1);
      return;
    }
    const limit = Math.max(1, maxRoomSessionPartySize);
    setPartySize(Math.max(1, Math.min(limit, value)));
    setWaitlistSuccessMessage(null);
    setWaitlistError(null);
  };

  const handleSendVerificationCode = async () => {
    if (!rageRoom?.public?.requestVerificationCode) {
      setCodeRequestError('Verification service is not available right now.');
      return;
    }
    if (!isPhoneNumberValid) {
      setCodeRequestError('Enter a valid 10-digit US phone number before requesting a code.');
      return;
    }
    const formattedPhone = formatPhoneNumberForRequest(customerDetails.phoneNumber);
    if (!formattedPhone) {
      setCodeRequestError('Enter a valid 10-digit US phone number before requesting a code.');
      return;
    }
    setWaitlistSuccessMessage(null);
    setWaitlistError(null);
    setSendCodeLoading(true);
    setCodeRequestMessage(null);
    setCodeRequestError(null);
    try {
      const response = await rageRoom.public.requestVerificationCode({ phoneNumber: formattedPhone });
      const message =
        response?.message || response?.data?.message || 'Verification code sent via SMS.';
      setCodeRequestMessage(message);
    } catch (error) {
      setCodeRequestError(
        getFriendlyErrorMessage(
          error,
          'We could not send a verification code right now. Please try again shortly.'
        )
      );
      setCodeRequestMessage(null);
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleVerifyCustomer = async () => {
    if (!rageRoom?.public?.verifyCustomer) {
      setVerificationError('Verification service is not available right now.');
      return;
    }
    const code = customerDetails.verificationCode.trim();
    if (!isPhoneNumberValid || !code) {
      setVerificationError('Enter your 10-digit phone number and verification code before verifying.');
      return;
    }
    const formattedPhone = formatPhoneNumberForRequest(customerDetails.phoneNumber);
    if (!formattedPhone) {
      setVerificationError('Enter a valid 10-digit US phone number before verifying.');
      return;
    }
    setVerificationLoading(true);
    setVerificationError(null);
    try {
      const response = await rageRoom.public.verifyCustomer({
        phoneNumber: formattedPhone,
        verificationCode: code,
      });
      const verification = response?.verification || response?.data?.verification;
      if (!verification?.token) {
        throw new Error('Verification token was not returned. Please try again.');
      }
      setVerificationToken(verification.token);
      setVerificationStatus(verification.status || 'approved');
      setVerificationExpiresAt(verification.expiresAt ? new Date(verification.expiresAt) : null);
    } catch (error) {
      resetVerificationState();
      setSelectedSlot(null);
      setVerificationError(
        getFriendlyErrorMessage(
          error,
          'We were unable to verify that code. Please try again in a moment.'
        )
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleTermsAcknowledgementChange = (event) => {
    setHasAcknowledgedTerms(event.target.checked);
  };

  const handleAgeRequirementChange = (event) => {
    setHasConfirmedAgeRequirement(event.target.checked);
  };

  const handleSubmitBooking = async (event) => {
    event.preventDefault();

    if (!selectedSlot) {
      setBookingError('Please select a time slot before booking.');
      return;
    }

    if (!isVerificationComplete || !verificationToken) {
      setBookingError('Please verify your phone number before confirming your booking.');
      return;
    }

    if (!isPhoneNumberValid) {
      setBookingError('Enter a valid 10-digit US phone number before booking.');
      return;
    }

    if (!hasAcknowledgedTerms) {
      setBookingError('Please acknowledge the terms and privacy disclosure before booking.');
      return;
    }

    if (!hasConfirmedAgeRequirement) {
      setBookingError('You must confirm that you are 18 years of age or older before booking.');
      return;
    }

    const formattedPhone = formatPhoneNumberForRequest(customerDetails.phoneNumber);
    if (!formattedPhone) {
      setBookingError('Enter a valid 10-digit US phone number before booking.');
      return;
    }

    if (partySize > selectedSlotRemainingCapacity) {
      setBookingError(
        'This session no longer has enough spots for your crew. Please pick another time or reduce your party size.'
      );
      return;
    }

    if (!rageRoom?.public?.bookRoom) {
      setBookingError('Booking service is not available right now.');
      return;
    }

    setBookingLoading(true);
    setBookingError(null);

    try {
      const slotQuantity = Math.max(1, Math.min(partySize, maxRoomSessionPartySize));

      const compiledNotes = [`Party size: ${slotQuantity}`];
      if (customerDetails.specialNote.trim()) {
        compiledNotes.push(customerDetails.specialNote.trim());
      }

      const bookingPayload = {
        locationId: selectedSlot.locationId,
        roomId: selectedSlot.roomId,
        date: selectedSlot.date,
        sessionLabel: selectedSlot.sessionLabel,
        timeSlot: selectedSlot.timeSlot,
        slotQuantity,
        customer: {
          name: customerDetails.name.trim(),
          email: customerDetails.email.trim(),
          phoneNumber: formattedPhone,
        },
        verificationToken,
        specialNote: compiledNotes.join(' • '),
      };

      if (slotQuantity === 1 && selectedSlot.slotLabel) {
        bookingPayload.slotLabel = selectedSlot.slotLabel;
      }

      const bookingResponse = await rageRoom.public.bookRoom(bookingPayload);

      const allocatedSlots = allocatedSlotsFromResponse(bookingResponse);
      const resolvedGuestCount = resolveGuestCountFromResponse(bookingResponse, slotQuantity);
      const bookingRecord = bookingFromResponse(bookingResponse);

      const successMessageParts = [
        `Your smash squad is confirmed for ${selectedSlot.roomName} on ${formatDisplayDate(
          selectedSlot.date
        )} at ${formatDisplayTime(selectedSlot.timeSlot)}.`,
        `Party of ${resolvedGuestCount}.`,
      ];

      if (allocatedSlots.length) {
        successMessageParts.push(`Slots locked: ${allocatedSlots.join(', ')}.`);
      }

      const confirmationState = {
        confirmationMessage: successMessageParts.join(' '),
        roomName: selectedSlot.roomName,
        city: selectedSlot.city || selectedRoom?.city || '',
        locationName:
          bookingRecord?.locationName ||
          selectedSlot.locationName ||
          selectedRoom?.locationName ||
          '',
        locationAddress:
          bookingRecord?.locationAddress ||
          selectedSlot.locationAddress ||
          selectedRoom?.locationAddress ||
          '',
        date: selectedSlot.date,
        timeSlot: selectedSlot.timeSlot,
        sessionLabel: selectedSlot.sessionLabel,
        resolvedGuestCount,
        allocatedSlots,
        bookingId:
          bookingRecord?.id ||
          bookingRecord?.bookingId ||
          bookingRecord?._id ||
          bookingRecord?.referenceId ||
          null,
      };

      setCustomerDetails({ ...INITIAL_CUSTOMER_DETAILS });
      setPartySize(1);
      setSelectedSlot(null);
      setSelectedRoomId('');
      setSelectedDate('');
      resetVerificationState();
      setVerificationError(null);
      setCodeRequestMessage(null);
      setHasAcknowledgedTerms(false);
      setHasConfirmedAgeRequirement(false);
      setWaitlistSuccessMessage(null);
      setWaitlistError(null);
      setWaitlistLoading(false);

      navigate('/book/confirmed', { state: confirmationState });
    } catch (error) {
      console.log(error);
      setBookingError(
        getFriendlyErrorMessage(
          error,
          'We could not confirm your booking right now. Please try again shortly.'
        )
      );
    } finally {
      setBookingLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!rageRoom?.public?.joinWaitlist) {
      setWaitlistError('Waitlist service is not available right now.');
      return;
    }
    if (!selectedRoom) {
      setWaitlistError('Select a rage room before joining the waitlist.');
      return;
    }
    if (!isVerificationComplete || !verificationToken) {
      setWaitlistError('Verify your phone number before joining the waitlist.');
      return;
    }
    if (!isPhoneNumberValid) {
      setWaitlistError('Enter a valid 10-digit US phone number before joining the waitlist.');
      return;
    }
    if (!hasAcknowledgedTerms) {
      setWaitlistError('Please acknowledge the terms and privacy disclosure before joining.');
      return;
    }
    if (!hasConfirmedAgeRequirement) {
      setWaitlistError('You must confirm that you are 18 years of age or older before joining.');
      return;
    }
    if (!customerDetails.name.trim() || !customerDetails.email.trim()) {
      setWaitlistError('Provide your name and email before joining the waitlist.');
      return;
    }
    const formattedPhone = formatPhoneNumberForRequest(customerDetails.phoneNumber);
    if (!formattedPhone) {
      setWaitlistError('Enter a valid 10-digit US phone number before joining the waitlist.');
      return;
    }

    const slotQuantity = Math.max(1, Math.min(partySize, maxRoomSessionPartySize));
    const waitlistPayload = {
      locationId: selectedRoom.locationId,
      roomId: selectedRoom.id || selectedRoomId,
      partySize: slotQuantity,
      slotQuantity,
      customer: {
        name: customerDetails.name.trim(),
        email: customerDetails.email.trim(),
        phoneNumber: formattedPhone,
      },
      specialNote: customerDetails.specialNote.trim() || undefined,
      verificationToken,
      waitlistSource: 'booking-page-no-availability',
      date: selectedRoom.date || undefined,
    };

    setWaitlistLoading(true);
    setWaitlistError(null);
    setWaitlistSuccessMessage(null);

    try {
      const response = await rageRoom.public.joinWaitlist(waitlistPayload);
      const waitlistEntry = response?.waitlistEntry;
      const cityName = waitlistEntry?.city || selectedRoom.city || selectedCity || 'this location';
      const normalizedWaitlistDate =
        typeof waitlistEntry?.date === 'string'
          ? waitlistEntry.date.split('T')[0]
          : waitlistEntry?.date || selectedRoom.date || null;
      const displayDate = normalizedWaitlistDate
        ? formatDisplayDate(normalizedWaitlistDate)
        : selectedRoom.displayDate || null;
      const displayTime = waitlistEntry?.requestedTimeSlot
        ? formatDisplayTime(waitlistEntry.requestedTimeSlot)
        : null;
      const scheduleDescriptor = [displayDate, displayTime].filter(Boolean).join(' ');
      const messageParts = [
        `You're on the waitlist for ${selectedRoom.name} in ${cityName}.`,
        scheduleDescriptor
          ? `We'll notify you if a ${scheduleDescriptor} slot opens up.`
          : 'We will reach out as soon as a compatible slot opens up.',
      ];
      const successMessage = messageParts.filter(Boolean).join(' ').trim();
      setWaitlistSuccessMessage(successMessage || WAITLIST_SUCCESS_FALLBACK);
    } catch (error) {
      setWaitlistError(
        getFriendlyErrorMessage(
          error,
          'We could not add you to the waitlist. Please try again shortly.'
        )
      );
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handlePrimaryAction = (event) => {
    if (shouldUseWaitlistAction) {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      handleJoinWaitlist();
      return;
    }
    handleSubmitBooking(event);
  };

  const primaryActionDisabled = shouldUseWaitlistAction
    ? !canJoinWaitlist || waitlistLoading
    : !canSubmitBooking || bookingLoading;

  const primaryActionLabel = shouldUseWaitlistAction
    ? waitlistLoading
      ? 'Joining Waitlist...'
      : 'Join Waitlist'
    : bookingLoading
    ? 'Booking...'
    : 'Confirm Booking';

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Stack spacing={4}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                mb: 3,
              }}
            >
              <Box
                component="img"
                src={PLUTO_LOGO_PATH}
                alt="Pluto TV logo"
                sx={{
                  height: { xs: 56, sm: 72, md: 160 },
                  width: 'auto',
                  filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.6))',
                }}
              />
            </Box>
            <Chip
              label="30-Min Private Rage Sessions"
              color="primary"
              sx={{ mb: 2, fontWeight: 600 }}
            />
            <Typography variant="h3" gutterBottom>
              Book a Rage Room for Free courtesy of Pluto TV
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
              Choose your city, lock in a room, and reserve a 30-minute smash session for up to four
              people. All availability is live and updates the moment bookings are confirmed.
            </Typography>
          </Box>

          {availabilityError && (
            <Alert severity="error" onClose={() => setAvailabilityError(null)}>
              {availabilityError}
            </Alert>
          )}

          {roomsError && (
            <Alert severity="warning" onClose={() => setRoomsError(null)}>
              {roomsError}
            </Alert>
          )}

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Typography variant="h5">1. Choose a City</Typography>
                  <Button
                    variant="outlined"
                    onClick={refreshData}
                    disabled={isCityListLoading}
                  >
                    Refresh Availability
                  </Button>
                </Stack>
                {isCityListLoading ? (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : cities.length === 0 ? (
                  <Typography color="text.secondary">
                    No public rage room drops are available right now. Check back soon or refresh to
                    unlock the waitlist as new cities appear.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    {cities.map((city) => {
                      const isSelected = selectedCity === city;
                      return (
                        <Chip
                          key={city}
                          label={city}
                          color={isSelected ? 'warning' : 'default'}
                          variant="outlined"
                          onClick={() => handleCitySelection(city)}
                          sx={{
                            height: 36,
                            fontWeight: 600,
                            borderWidth: isSelected ? 2 : 1,
                            borderColor: isSelected ? 'warning.main' : 'divider',
                            backgroundColor: isSelected ? 'rgba(255, 193, 7, 0.16)' : 'transparent',
                            transition: 'border-color 0.2s ease, background-color 0.2s ease',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                2. Pick a Date and Rage Room
              </Typography>
              {!selectedCity ? (
                <Typography color="text.secondary">
                  Select a city above to see which rooms are open for booking.
                </Typography>
              ) : roomsByDateForSelectedCity.length > 0 ? (
                <Stack spacing={3}>
                  {roomsByDateForSelectedCity.map((group) => (
                    <Box
                      key={group.date}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {group.displayDate}
                      </Typography>
                      <Grid container spacing={2}>
                        {group.rooms.map((room) => {
                          const slotDescriptor = room.nextSlot
                            ? formatDisplayTime(room.nextSlot.timeSlot)
                            : 'Live slots pending';
                          const availabilityChipColor =
                            room.availableCount === 0
                              ? 'default'
                              : room.availableCount > 5
                              ? 'success'
                              : 'warning';
                          const locationLabel = room.locationName
                            ? `${room.locationName} - ${room.city}`
                            : room.city;
                          const isSelected =
                            selectedRoomId === room.id && selectedDate === group.date;

                          return (
                            <Grid item xs={12} md={6} key={`${room.id}-${group.date}`}>
                              <Card
                                onClick={() => handleRoomSelection(room.id, group.date)}
                                sx={{
                                  height: '100%',
                                  borderWidth: isSelected ? 2 : 1,
                                  borderStyle: 'solid',
                                  borderColor: isSelected ? 'primary.main' : 'divider',
                                  cursor: 'pointer',
                                  transition: 'border-color 0.2s ease, transform 0.2s ease',
                                  transform: isSelected ? 'translateY(-2px)' : 'none',
                                }}
                                variant="outlined"
                              >
                                <CardContent>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                  >
                                    <Typography variant="h6">{room.name}</Typography>
                                    <Chip
                                      label={`${room.availableCount} slots`}
                                      size="small"
                                      color={availabilityChipColor}
                                    />
                                  </Stack>
                                  <Typography color="text.secondary" sx={{ mb: 1 }}>
                                    {locationLabel} • Next slot {slotDescriptor}
                                  </Typography>
                                  <Typography color="text.secondary" sx={{ mb: 1 }}>
                                    {room.locationAddress || 'Address details will be shared soon.'}
                                  </Typography>
                                  <Typography color="primary" variant="body2">
                                    {room.availableCount > 0
                                      ? 'Tap to review all 30-minute sessions in this room for this date.'
                                      : 'Tap to verify your crew and join the waitlist for this date.'}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              ) : directoryRoomsForSelectedCity.length === 0 ? (
                <Typography color="text.secondary">
                  We have not posted rage rooms for {selectedCity} yet. Refresh for updates or pick
                  another city to continue.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Live drops for {selectedCity} are currently full. Select a room below to verify
                    your crew and jump on the waitlist as soon as availability returns.
                  </Alert>
                  <Grid container spacing={2}>
                    {directoryRoomsForSelectedCity.map((room) => {
                      const slotDescriptor = room.nextSlot
                        ? `${formatDisplayDate(room.nextSlot.date)} @ ${formatDisplayTime(
                            room.nextSlot.timeSlot
                          )}`
                        : 'Live slots pending';
                      const availabilityChipColor =
                        room.availableCount === 0
                          ? 'default'
                          : room.availableCount > 5
                          ? 'success'
                          : 'warning';
                      const locationLabel = room.locationName
                        ? `${room.locationName} - ${room.city}`
                        : room.city;
                      const isSelected = selectedRoomId === room.id && !selectedDate;

                      return (
                        <Grid item xs={12} md={6} key={room.id}>
                          <Card
                            onClick={() => handleRoomSelection(room.id, room.date)}
                            sx={{
                              height: '100%',
                              borderWidth: isSelected ? 2 : 1,
                              borderStyle: 'solid',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              cursor: 'pointer',
                              transition: 'border-color 0.2s ease, transform 0.2s ease',
                              transform: isSelected ? 'translateY(-2px)' : 'none',
                            }}
                            variant="outlined"
                          >
                            <CardContent>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                              >
                                <Typography variant="h6">{room.name}</Typography>
                                <Chip
                                  label={`${room.availableCount} slots`}
                                  size="small"
                                  color={availabilityChipColor}
                                />
                              </Stack>
                              <Typography color="text.secondary" sx={{ mb: 1 }}>
                                {locationLabel} • Next slot {slotDescriptor}
                              </Typography>
                              <Typography color="text.secondary" sx={{ mb: 1 }}>
                                {room.locationAddress || 'Address details will be shared soon.'}
                              </Typography>
                              <Typography color="primary" variant="body2">
                                Tap to unlock verification and snag a waitlist spot once sessions are
                                released.
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card>
            <Box component="form" noValidate onSubmit={handlePrimaryAction}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  3. Confirm Your Crew
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {crewAlertMessage}
                </Alert>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                  We will provide accessibility and reasonable disability accommodations for this event.
                  Please contact{' '}
                  <Link href="mailto:accessibilityservices@paramountplus.com" underline="hover">
                    accessibilityservices@paramountplus.com
                  </Link>{' '}
                  at least 72 hours prior to the event so we can best address your needs.
                </Alert>
                {verificationError && (
                  <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setVerificationError(null)}>
                    {verificationError}
                  </Alert>
                )}
                {!selectedRoom ? (
                  <Alert severity="info">
                    Choose a rage room in Step 2 to unlock phone verification and crew details.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <TextField
                          label="Phone Number"
                          type="tel"
                          value={customerDetails.phoneNumber}
                          onChange={handleCustomerChange('phoneNumber')}
                          required
                          fullWidth
                          error={Boolean(customerDetails.phoneNumber) && !isPhoneNumberValid}
                          helperText={phoneHelperText}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            maxLength: PHONE_NUMBER_LENGTH,
                          }}
                        />
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems="center"
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleSendVerificationCode}
                            disabled={sendCodeLoading || !isPhoneNumberValid}
                          >
                            {sendCodeLoading ? 'Sending...' : 'Send Code'}
                          </Button>
                          {codeRequestMessage && (
                            <Chip label={codeRequestMessage} color="primary" size="small" />
                          )}
                          {codeRequestError && (
                            <Typography variant="caption" color="error.main">
                              {codeRequestError}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <TextField
                          label="Verification Code"
                          value={customerDetails.verificationCode}
                          onChange={handleCustomerChange('verificationCode')}
                          required
                          fullWidth
                          helperText={verificationHelperText}
                        />
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems="center"
                        >
                          <Button
                            type="button"
                            variant={isVerificationComplete ? 'contained' : 'outlined'}
                            color={isVerificationComplete ? 'primary' : 'primary'}
                            onClick={handleVerifyCustomer}
                            disabled={
                              verificationLoading ||
                              !isPhoneNumberValid ||
                              !customerDetails.verificationCode.trim()
                            }
                          >
                            {verificationLoading
                              ? 'Verifying...'
                              : isVerificationComplete
                              ? 'Reverify Code'
                              : 'Confirm Code'}
                          </Button>
                          {isVerificationComplete && (
                            <Chip label="Verified" color="primary" size="small" />
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Full Name"
                        value={customerDetails.name}
                        onChange={handleCustomerChange('name')}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        type="email"
                        value={customerDetails.email}
                        onChange={handleCustomerChange('email')}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Party Size"
                        value={partySize}
                        onChange={handlePartySizeChange}
                        fullWidth
                        helperText={partySizeHelperText}
                      >
                        {Array.from({ length: maxRoomSessionPartySize }, (_, index) => index + 1).map(
                          (count) => (
                            <MenuItem key={count} value={count}>
                              {count} {count === 1 ? 'Person' : 'People'}
                            </MenuItem>
                          )
                        )}
                      </TextField>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Box>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Step 4. Lock a 30-Minute Slot
              </Typography>
              {!selectedRoom ? (
                <Typography color="text.secondary">
                  Pick a rage room and date above to browse the remaining sessions for that location.
                </Typography>
              ) : !isVerificationComplete ? (
                <Alert severity="warning">
                  Verify your phone number above to unlock live availability for {selectedRoom.name}
                  {selectedRoom.displayDate ? ` on ${selectedRoom.displayDate}` : ''}.
                </Alert>
              ) : !hasAvailableSlots ? (
                <Stack spacing={2}>
                  {waitlistSuccessMessage && (
                    <Alert
                      severity="success"
                      onClose={() => setWaitlistSuccessMessage(null)}
                    >
                      {waitlistSuccessMessage}
                    </Alert>
                  )}
                  {waitlistError && (
                    <Alert severity="error" onClose={() => setWaitlistError(null)}>
                      {waitlistError}
                    </Alert>
                  )}
                  <Typography color="text.secondary">
                    This room is fully committed right now. Complete Steps 1-3 and then tap "Join Waitlist"
                    below to be notified as soon as compatible sessions open up.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We will reach out using your verified phone number and email when a matching session is
                    released.
                  </Typography>
                </Stack>
              ) : (
                <Box sx={{ mt: 1, width: '100%' }}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      mx: 'auto',
                      height: 400,
                      overflowY: 'auto',
                      backgroundColor: 'background.paper',
                      p: 1.5,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(244,243,248,0.05)',
                        borderRadius: 999,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,242,0,0.45)',
                        borderRadius: 999,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        justifyContent: 'flex-start',
                      }}
                    >
                      {availableSlotsForRoom.map((slot) => {
                        const slotId = getSlotIdentifier(slot);
                        const isSelected =
                          selectedSlot && getSlotIdentifier(selectedSlot) === slotId;
                        const remainingCapacity = resolveRemainingCapacity(slot);
                        const exceedsPartySize = partySize > remainingCapacity;
                        const isSoldOut = remainingCapacity <= 0;
                        const slotDisabled = isSoldOut || exceedsPartySize;

                        return (
                          <Card
                            key={slotId}
                            variant="outlined"
                            sx={{
                              borderColor: isSelected
                                ? 'primary.main'
                                : slotDisabled
                                ? 'rgba(255,255,255,0.12)'
                                : 'divider',
                              borderRadius: 2,
                              flex: '1 1 260px',
                              minWidth: 220,
                              maxWidth: 320,
                              opacity: slotDisabled ? 0.5 : 1,
                              transition: 'border-color 0.2s ease, opacity 0.2s ease',
                            }}
                          >
                            <CardContent sx={{ py: 1.5, px: 2 }}>
                              <Stack direction="column" spacing={0.5} sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {formatDisplayDate(slot.date)} • {formatDisplayTime(slot.timeSlot)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={slotDisabled ? 'text.disabled' : 'text.secondary'}
                                >
                                  {slot.sessionLabel} • {remainingCapacity} spot
                                  {remainingCapacity === 1 ? '' : 's'} left this session
                                </Typography>
                                {exceedsPartySize && (
                                  <Typography variant="caption" color="warning.main">
                                    Needs {partySize} spot{partySize === 1 ? '' : 's'} • only{' '}
                                    {remainingCapacity} left
                                  </Typography>
                                )}
                                {isSoldOut && (
                                  <Typography variant="caption" color="error.main">
                                    Fully booked
                                  </Typography>
                                )}
                              </Stack>
                              <Button
                                variant={isSelected ? 'contained' : 'outlined'}
                                color={isSelected ? 'primary' : 'inherit'}
                                onClick={() => handleSlotSelection(slot)}
                                disabled={slotDisabled}
                                size="small"
                                fullWidth
                              >
                                {isSelected
                                  ? 'Selected'
                                  : isSoldOut
                                  ? 'Full'
                                  : exceedsPartySize
                                  ? 'Not Enough Spots'
                                  : 'Select Slot'}
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mt: 3 }}
          >
            <Stack spacing={1} sx={{ maxWidth: 560 }}>
              {bookingError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBookingError(null)}>
                  {bookingError}
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={hasAcknowledgedTerms}
                    onChange={handleTermsAcknowledgementChange}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I have read and agreed to the terms for participating, event rules, etc., and acknowledge
                    that my information will be used in accordance with the{' '}
                    <Link
                      href="https://privacy.paramount.com/policy"
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                    >
                      privacy policy
                    </Link>
                    .
                  </Typography>
                }
                sx={{ alignItems: 'flex-start' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={hasConfirmedAgeRequirement}
                    onChange={handleAgeRequirementChange}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I confirm that I am at least 18 years of age.
                  </Typography>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Stack>
            <Button
              variant="contained"
              size="large"
              onClick={handlePrimaryAction}
              disabled={primaryActionDisabled}
            >
              {primaryActionLabel}
            </Button>
          </Stack>

          <Footer />
        </Stack>
      </Container>
    </>
  );
};

export default BookingPage;